import {CommonModule, isPlatformBrowser} from '@angular/common';
import {Component, forwardRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {MessageModule} from 'primeng/message';
import {catchError, map, of, Subject, Subscription, switchMap} from 'rxjs';
import {LanguageService} from '../../core/services/language.service';
import {MapModule} from '../map/map.module';
import {MapService} from '../map/map.service';

export interface GeocodedPlace {
  display_name: string;
  lat: string | number;
  lon: string | number;
  address?: {country_code?: string; [key: string]: unknown};
  [key: string]: unknown;
}

/** Keep the existing event/listing form and API payload shape. */
export interface AddressSelection {label: string; value: GeocodedPlace;}

@Component({
  selector: 'app-address-picker', standalone: true,
  host: {class: 'block w-full p-fluid'},
  imports: [CommonModule, FormsModule, TranslateModule, AutoCompleteModule, MessageModule, MapModule],
  providers: [MapService, {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AddressPickerComponent), multi: true}],
  templateUrl: './address-picker.component.html',
})
export class AddressPickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() inputId = 'address-picker';
  @Input() label = '';
  @Input() placeholder = '';
  @Input() countries: string[] = [];
  @Input() readonly = false;
  @Input() hideAutocomplete = false;
  @Input() selectedZoom = 1;
  @Input() draggable = false;
  @Input() showNavigationControls = false;
  selection: AddressSelection[] = [];
  suggestions: AddressSelection[] = [];
  disabled = false;
  error = '';
  private readonly queries = new Subject<string | null>();
  private readonly subscriptions = new Subscription();
  private onChange: (value: AddressSelection[]) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private readonly maps: MapService, private readonly language: LanguageService,
    @Inject(PLATFORM_ID) private readonly platform: object) {}

  ngOnInit(): void {
    this.subscriptions.add(this.queries.pipe(
      switchMap(query => query === null ? of([] as AddressSelection[]) :
        this.maps.findPlaces(query, this.language.getLanguage()).pipe(
          map((places: GeocodedPlace[]) => places.filter(place => this.accepts(place))
            .map(place => ({label: place.display_name, value: place}))),
          catchError(() => { this.error = 'shared.address_picker.search_error'; return of([] as AddressSelection[]); }),
        )),
    ).subscribe(suggestions => { this.suggestions = suggestions; }));
  }

  ngOnDestroy(): void { this.subscriptions.unsubscribe(); this.queries.complete(); }

  get point(): {lat: number; lon: number} | null {
    const place = this.selection[0]?.value;
    return place && this.validCoordinates(place) ? {lat: Number(place.lat), lon: Number(place.lon)} : null;
  }

  search(event: {query: string}): void {
    if (!isPlatformBrowser(this.platform) || this.disabled || this.readonly || this.hideAutocomplete) {
      this.queries.next(null); return;
    }
    this.error = '';
    const query = event.query.trim();
    this.queries.next(query.length >= 2 ? query : null);
  }

  change(value: AddressSelection[] | null): void {
    if (this.disabled || this.readonly || this.hideAutocomplete) return;
    const next = value?.slice(-1) || [];
    if (next.length && !this.accepts(next[0]?.value)) {
      this.error = 'shared.address_picker.invalid_address'; return;
    }
    this.selection = next;
    this.error = '';
    this.queries.next(null);
    this.onChange([...next]);
    this.onTouched();
  }

  writeValue(value: AddressSelection[] | null): void { this.selection = Array.isArray(value) ? value.slice(-1) : []; }
  registerOnChange(fn: (value: AddressSelection[]) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; if (disabled) this.queries.next(null); }

  private accepts(place?: GeocodedPlace): boolean {
    return !!place && this.validCoordinates(place) && (!this.countries.length ||
      this.countries.some(country => country.toUpperCase() === place.address?.country_code?.toUpperCase()));
  }

  private validCoordinates(place: GeocodedPlace): boolean {
    return String(place.lat ?? '').trim() !== '' && String(place.lon ?? '').trim() !== '' &&
      Number.isFinite(Number(place.lat)) && Number.isFinite(Number(place.lon)) &&
      Math.abs(Number(place.lat)) <= 90 && Math.abs(Number(place.lon)) <= 180;
  }
}
